/*
 * The queue behind the For-you feed's reporting.
 *
 * Everything here is arithmetic over a shape: what was on screen, for how long,
 * and what was done about it — folded into the smallest set of facts that can
 * be sent. No React, no React Native, no session: the hook that gives this a
 * clock and an agent is `#/state/feed-ranking`, and this module is the part that
 * can be reasoned about on its own.
 *
 * Three decisions shape it:
 *
 *  - **The queue is a fold, not a log.** Every measurement is kept as the
 *    maximum for its post, exactly as the server's own folding does, so a
 *    re-render that re-reports half a second after two seconds cannot undo the
 *    two. Re-sending is free: the endpoint upserts.
 *  - **A sighting has a beginning and an end.** `markVisible` opens a dwell,
 *    `markHidden` closes it, and `checkpoint` folds the time so far into the
 *    queue without ending it — which is what a flush needs, because a post still
 *    being read has a reading to report.
 *  - **Queues are bounded.** A viewer who scrolls a thousand posts without a
 *    single flush succeeding should not cost a thousand posts of memory, and the
 *    oldest sightings are the ones the feed has already moved past.
 */
import {DISCOVER_FEED_URI} from '#/lib/constants'

/** The endpoint these reports are sent to. */
export const FOR_YOU_EVENTS_METHOD = 'app.taderok.feed.recordEvents'

/** Which surface a sighting happened on. */
export const SURFACE_FEED = 'feed'

/**
 * How many posts a queue will hold before the oldest is dropped.
 *
 * A bound rather than a promise: the oldest sightings are the least useful — the
 * feed has already moved past them.
 */
export const MAX_QUEUED_POSTS = 200

/**
 * How many posts one delivery of the For-you feed carries.
 *
 * The server decides the hundred; the phone asks for a slice of it. Five keeps
 * the payload and the arrival small, and it is a *ceiling* on the server too, so
 * asking for more would only be trimmed. Raise this (or the server's
 * `BSKY_FOR_YOU_DELIVERY_CHUNK`) to compare chunk sizes — the ranked hundred and
 * its order do not depend on it.
 */
export const DELIVERY_CHUNK = 5

/**
 * The feed whose reports this client sends.
 *
 * One URI, not three. The list used to hold the video and staging URIs as well,
 * because all three named the same generator record; now that the ranked feed
 * has a record of its own (`FOR_YOU_FEED_URI`) those names belong to other
 * feeds, and naming them here would claim reports for a feed nothing ranked. It
 * is a list, and the check is an `includes` rather than an equality, because a
 * feed descriptor may carry parameters after the URI.
 */
const FOR_YOU_FEED_URIS = [DISCOVER_FEED_URI]

export function isForYouFeed(feedDescriptor?: string | null): boolean {
  if (!feedDescriptor) return false
  return FOR_YOU_FEED_URIS.some(uri => feedDescriptor.includes(uri))
}

/**
 * The pages Home draws, in order.
 *
 * The reader's pinned feeds as they pinned them, with the system For-you page in
 * front when they have not pinned that feed themselves. Two rules decide it:
 *
 *  - **The system page is only for a feed the reader does not own.** If For You
 *    is already pinned, that page is the one that shows and nothing is added —
 *    a reader must never see the same feed twice. This is a question about the
 *    ranked feed alone: pinning Trending is not pinning For You, so it takes
 *    nothing away and the system page is still added in front.
 *  - **Nothing here is written down.** The list is the screen's, not the
 *    account's: no pin is added, none is reordered, and the array handed in is
 *    never touched.
 */
export function homeFeedPages<T extends {feedDescriptor: string}>(
  pinnedFeeds: readonly T[],
  systemForYou: T,
): T[] {
  if (pinnedFeeds.some(feed => isForYouFeed(feed.feedDescriptor))) {
    return [...pinnedFeeds]
  }
  return [systemForYou, ...pinnedFeeds]
}

/**
 * Which of Home's pages a reader lands on.
 *
 * Their stored choice first — a reader who has chosen a feed keeps it — then the
 * ranked feed, then whatever happens to be first. The third case is the only
 * fallback, and it belongs to a reader whose choice is gone: a pinned feed since
 * unpinned, or a `?feed=` link to a feed that no longer exists. Falling back to
 * For You rather than to an arbitrary tab is the point of this function.
 *
 * `-1` means there is no page at all, which the signed-in screen never sees and
 * the caller clamps.
 */
export function homeFeedIndex(
  pages: readonly {feedDescriptor: string}[],
  explicitSelection?: string | null,
): number {
  if (explicitSelection) {
    const chosen = pages.findIndex(
      page => page.feedDescriptor === explicitSelection,
    )
    if (chosen !== -1) return chosen
  }
  return pages.findIndex(page => isForYouFeed(page.feedDescriptor))
}

/**
 * What an empty Home page says.
 *
 * Three situations look identical on screen — a blank list — and mean different
 * things, so the page with nothing to show is the page that has to say which one
 * it is:
 *
 *  - `'for-you'`: the ranked feed answered with nothing. Its own machinery is
 *    what decides that (the ranked pool is empty, or this reader has already
 *    been shown everything eligible), and the copy has to say so rather than
 *    borrow the following feed's advice to go and follow people — advice that is
 *    simply wrong here, because a first-party ranked feed has nobody to follow.
 *    This is the state a fresh deployment is in until the ranking has been
 *    refreshed, and it must not look like the feed is still loading.
 *  - `'no-feeds-pinned'`: the reader has pinned nothing, so the useful thing to
 *    offer is the way to pin something.
 *  - `'feed'`: any other feed, which keeps the empty state it already had.
 */
export type HomeEmptyState = 'no-feeds-pinned' | 'for-you' | 'feed'

export function homeEmptyState(input: {
  isSystemForYou: boolean
  pinnedFeedCount: number
}): HomeEmptyState {
  if (!input.isSystemForYou) return 'feed'
  return input.pinnedFeedCount === 0 ? 'no-feeds-pinned' : 'for-you'
}

// ── the wire ─────────────────────────────────────────────────────────────────

export type SeenReportWire = {
  postUri: string
  seenAt: string
  visibleMs?: number
  progressBp?: number
  surface?: string
  position?: number
}

export type ConsumptionReportWire = {
  postUri: string
  activeMs?: number
  progressBp?: number
  completed?: boolean
}

/**
 * The deliberate signals, spelled once.
 *
 * The server reads these by name: the ones that mean *less of this* exclude the
 * post from the next page, and `share` is a positive weight. The words are the
 * endpoint's own vocabulary, not this client's invention, which is why they are
 * a union rather than a plain string.
 */
export type FeedSignalKind =
  | 'not_interested'
  | 'hide'
  | 'mute'
  | 'report'
  | 'skip'
  | 'share'

export type SignalReportWire = {
  postUri: string
  kind: FeedSignalKind
  createdAt?: string
}

export type FeedEventBatch = {
  seen: SeenReportWire[]
  consumption: ConsumptionReportWire[]
  negatives: SignalReportWire[]
}

// ── the queue ────────────────────────────────────────────────────────────────

/**
 * Sightings and signals, held until they are sent.
 *
 * Every queue is a `Map` keyed by what makes the fact unique — the post, and for
 * signals the post and the kind — so a post reported twice is one entry, and the
 * insertion order survives, which is what makes the oldest-drops-first bound
 * meaningful.
 */
/**
 * A post currently on screen, and how long it has been there in total.
 *
 * The total is carried separately from the start time because a dwell can be
 * reported more than once — a flush while the post is still being read folds in
 * the time so far and restarts the clock — and the *sum* of those folds is the
 * reading. Folding a maximum over them would report only the longest single
 * stretch between flushes, which is not the same thing at all.
 */
type Dwell = {
  start: number
  total: number
}

export class ForYouReporter {
  private readonly dwells = new Map<string, Dwell>()
  private readonly seen = new Map<string, SeenReportWire>()
  private readonly consumption = new Map<string, ConsumptionReportWire>()
  private readonly signals = new Map<string, SignalReportWire>()

  constructor(private readonly clock: () => number = () => Date.now()) {}

  /** A post is on screen: remember when, and that it was shown. */
  markVisible(uris: readonly string[], position?: number): void {
    const now = this.clock()
    for (const uri of uris) {
      if (!uri) continue
      if (!this.dwells.has(uri)) {
        this.dwells.set(uri, {start: now, total: 0})
      }
      const existing = this.seen.get(uri)
      if (!existing) {
        this.put(this.seen, uri, {
          postUri: uri,
          seenAt: new Date(now).toISOString(),
          surface: SURFACE_FEED,
          ...(position === undefined ? {} : {position}),
        })
      } else if (position !== undefined && existing.position === undefined) {
        existing.position = position
      }
    }
  }

  /** A post left the screen: close its dwell and fold the reading in. */
  markHidden(uris: readonly string[]): void {
    const now = this.clock()
    for (const uri of uris) {
      const dwell = this.dwells.get(uri)
      if (dwell === undefined) continue
      this.dwells.delete(uri)
      this.addDwell(uri, Math.max(0, dwell.total + (now - dwell.start)))
    }
  }

  /**
   * Fold what is still on screen into the queue, without ending it.
   *
   * Called before a flush so a post that is still being read is sent with the
   * time it has had *so far* rather than not at all — and the dwell clock is
   * restarted, so the next flush adds only what came after this one.
   */
  checkpoint(): void {
    const now = this.clock()
    for (const [uri, dwell] of this.dwells) {
      dwell.total += Math.max(0, now - dwell.start)
      dwell.start = now
      this.addDwell(uri, dwell.total)
    }
  }

  /** A player's own measurement — a play, a progress, or a completed work. */
  reportConsumption(report: ConsumptionReportWire): void {
    if (!report.postUri) return
    const existing = this.consumption.get(report.postUri)
    if (!existing) {
      this.put(this.consumption, report.postUri, {...report})
      return
    }
    existing.activeMs = Math.max(existing.activeMs ?? 0, report.activeMs ?? 0)
    existing.progressBp = maxDefined(existing.progressBp, report.progressBp)
    existing.completed = Boolean(existing.completed || report.completed)
  }

  /** A deliberate signal about a post. */
  reportSignal(postUri: string, kind: FeedSignalKind): void {
    if (!postUri) return
    this.put(this.signals, `${postUri}\u0000${kind}`, {
      postUri,
      kind,
      createdAt: new Date(this.clock()).toISOString(),
    })
  }

  /** Everything queued, emptied. `null` when there is nothing to send. */
  drain(): FeedEventBatch | null {
    this.checkpoint()
    if (
      this.seen.size === 0 &&
      this.consumption.size === 0 &&
      this.signals.size === 0
    ) {
      return null
    }
    const batch: FeedEventBatch = {
      seen: [...this.seen.values()],
      consumption: [...this.consumption.values()],
      negatives: [...this.signals.values()],
    }
    this.clear()
    return batch
  }

  /** How many entries are waiting in any queue, for tests and diagnostics. */
  get pending(): number {
    return this.seen.size + this.consumption.size + this.signals.size
  }

  clear(): void {
    this.seen.clear()
    this.consumption.clear()
    this.signals.clear()
  }

  private addDwell(uri: string, ms: number): void {
    if (ms <= 0) return
    const seen = this.seen.get(uri)
    if (seen) {
      seen.visibleMs = Math.max(seen.visibleMs ?? 0, ms)
    }
    const consumption = this.consumption.get(uri)
    if (consumption) {
      consumption.activeMs = Math.max(consumption.activeMs ?? 0, ms)
    } else {
      this.put(this.consumption, uri, {postUri: uri, activeMs: ms})
    }
  }

  /** Insert, dropping the oldest entry when the queue is full. */
  private put<T>(queue: Map<string, T>, key: string, value: T): void {
    if (!queue.has(key) && queue.size >= MAX_QUEUED_POSTS) {
      const oldest = queue.keys().next()
      if (!oldest.done) queue.delete(oldest.value)
    }
    queue.set(key, value)
  }
}

function maxDefined(
  a: number | undefined,
  b: number | undefined,
): number | undefined {
  if (a === undefined) return b
  if (b === undefined) return a
  return Math.max(a, b)
}
